// 영웅 그레이박스: 몸(병합) + 망토 + 검. 상태(act/comboStep/attackPhase)로 절차 애니메이션을 만든다.
// 시간은 영웅 시계(히트스톱·일시정지에 멈춤)를 쓴다. 림 발광 + 반전 헐 외곽선.
import { ArmorTierBinding, WeaponTierBinding } from './kaelTier';
import * as THREE from 'three/webgpu';
import { COMBO, DODGE, PALETTE, SKILLS, SKILL_TUNING } from '../shared/config';
import { AwakenPhase, HeroAct, type GameView } from '../shared/types';
import type { GameClocks } from './clock';
import { colored, merge } from './geo';
import type { KaelModel } from './kael';
import { addHull, charMaterial, hullMaterial, RimControl, setEmissive, type EmissiveMat } from './materials';
import { angleDelta, clamp01, damp, easeInOut, easeOut, lerp, phaseOf, TAU } from './util';

/**
 * 공격·회피·스킬 중 몸 돌리기 최대 각속도(rad/영웅 초). 180°를 3틱(50ms)에 돈다.
 * 예전에는 한 틱에 바로 돌려(자동 조준 180°면 모델이 한 프레임에 뒤집혀) 칼끝이 한 틱에 최대 16m 튀었다.
 * 가장 짧은 선딜(70ms)보다 먼저 다 돌아 판정 순간의 방향은 그대로다. 히트스톱 동안은 영웅 시계를 따라 멈춘다.
 */
export const ACTION_TURN_RATE = Math.PI / (3 / 60);

const PIVOT_Y = 0.95; // 구르기·기울임의 회전 중심 높이
const IDLE_YAW = 0.5;
const IDLE_PITCH = 0.55;
const RIM_BASE = 0.55;

export class HeroVisual {
  private readonly armorTierBinding = new ArmorTierBinding();
  private readonly weaponTierBinding = new WeaponTierBinding();
  readonly root = new THREE.Group();
  readonly rim = new RimControl(PALETTE.crimson, RIM_BASE);
  private readonly lean = new THREE.Group();
  private readonly content = new THREE.Group();
  private readonly swordPivot = new THREE.Group();
  private readonly capePivot = new THREE.Group();
  private readonly mats: EmissiveMat[] = [];
  private readonly hullMat = hullMaterial(0.035);
  /** 외부(카메라·VFX)가 읽는 보간된 월드 위치 */
  x = 0; y = 0; z = 0;
  private readonly contactPosition = new THREE.Vector3();
  /** 논리 좌표와 분리된 표시 몸의 위치. root-motion 중에도 접촉 그림자는 몸 아래에 남는다. */
  get groundPosition(): THREE.Vector3 {
    return this.model ? this.model.getGroundPosition(this.contactPosition)
      : this.contactPosition.copy(this.root.position);
  }
  /** 각성 연출이 더하는 림 세기 */
  rimBoost = 0;
  /** 모델 각성 발광 색(c022: 카엘 진홍, 라이라 청록 — 렌더가 영웅이 바뀔 때 정한다) */
  glowHex = 0xff2a3d;
  /** 로드된 Kael 모델(없으면 캡슐 그레이박스) */
  model: KaelModel | null = null;

  private yaw = Math.PI;
  private yawInit = false;
  private swYaw = IDLE_YAW;
  private swPitch = IDLE_PITCH;
  private leanX = 0;
  private yOff = 0;
  private squash = 1;
  private capeX = 0.2;

  constructor() {
    const bodyMat = charMaterial(0xffffff, this.rim, { roughness: 0.55, metalness: 0.2, vertexColors: true });
    const capeMat = charMaterial(0xb3202f, this.rim, { roughness: 0.9 });
    const swordMat = charMaterial(0xffffff, this.rim, { roughness: 0.3, metalness: 0.6, vertexColors: true });
    this.mats.push(bodyMat, capeMat, swordMat);

    const hairCap = new THREE.SphereGeometry(0.36, 16, 12, 0, TAU, 0, Math.PI * 0.62);
    const bodyGeo = merge([
      colored(new THREE.CapsuleGeometry(0.4, 0.75, 6, 12), PALETTE.navy, 0, 0.95, 0),
      colored(new THREE.CylinderGeometry(0.43, 0.43, 0.1, 12), PALETTE.gold, 0, 0.72, 0),
      colored(new THREE.CylinderGeometry(0.3, 0.26, 0.45, 10), PALETTE.navy, 0, 0.25, 0),
      colored(new THREE.SphereGeometry(0.33, 16, 12), 0xf1d3b3, 0, 1.78, 0.02),
      colored(hairCap, 0xe9e9f2, 0, 1.82, -0.02),
      colored(new THREE.BoxGeometry(0.1, 0.1, 0.16), 0xf1d3b3, 0, 1.74, 0.33), // 정면 표시
    ]);
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.castShadow = true;
    addHull(body, this.hullMat);

    const cape = new THREE.Mesh(new THREE.BoxGeometry(0.78, 1.15, 0.07), capeMat);
    cape.position.set(0, -0.55, -0.02);
    cape.castShadow = true;
    addHull(cape, this.hullMat);
    this.capePivot.position.set(0, 1.5, -0.38);
    this.capePivot.add(cape);

    // 검: 어깨 피벗에서 +z 방향으로 뻗는다
    const swordGeo = merge([
      colored(new THREE.BoxGeometry(0.13, 0.05, 1.6), 0xd5dbe6, 0, 0, 1.05),
      colored(new THREE.BoxGeometry(0.4, 0.07, 0.1), PALETTE.gold, 0, 0, 0.22),
      colored(new THREE.BoxGeometry(0.09, 0.09, 0.32), PALETTE.navy, 0, 0, 0.02),
    ]);
    const sword = new THREE.Mesh(swordGeo, swordMat);
    sword.castShadow = true;
    addHull(sword, this.hullMat);
    this.swordPivot.position.set(0.5, 1.25, 0.1);
    this.swordPivot.rotation.order = 'YXZ';
    this.swordPivot.add(sword);

    this.content.position.y = -PIVOT_Y;
    this.content.add(body, this.capePivot, this.swordPivot);
    this.lean.position.y = PIVOT_Y;
    this.lean.add(this.content);
    this.root.add(this.lean);
  }

  /** Kael 모델로 바꾼다. 캡슐은 숨기고 루트는 위치·방향만 맡는다. 이미 붙은 모델이 있으면 장면에서 떼고 바꾼다(라이라 전용 몸 ↔ 카엘) */
  attachModel(m: KaelModel): void {
    if (this.model && this.model !== m) this.root.remove(this.model.root);
    this.model = m;
    this.root.add(m.root);
    this.lean.visible = false;
    // QA 계측: 측정기(qa/combat/scripts/feel-probe.mjs)가 칼끝·발 위치를 읽는다
    if (typeof location !== 'undefined' && (new URLSearchParams(location.search).has('qa') || import.meta.env.DEV)) (window as unknown as { __KAEL__?: unknown }).__KAEL__ = { model: m, view: this };
  }

  /** 몸 방향: 동작 중에는 ACTION_TURN_RATE로 빠르게(영웅 시계 heroDt), 대기·달리기는 부드럽게 따라간다 */
  private turn(act: number, facing: number, dt: number, heroDt: number): void {
    if (!this.yawInit) { this.yaw = facing; this.yawInit = true; return; }
    const d = angleDelta(this.yaw, facing);
    if (act === HeroAct.Attack || act === HeroAct.Dodge || act === HeroAct.Skill) {
      const step = ACTION_TURN_RATE * heroDt;
      this.yaw += Math.abs(d) <= step ? d : Math.sign(d) * step;
    } else this.yaw += d * damp(28, dt);
  }

  update(view: GameView, alpha: number, dt: number, clocks: GameClocks): void {
    const h = view.hero;
    this.x = h.prevX + (h.x - h.prevX) * alpha;
    this.y = h.prevY + (h.y - h.prevY) * alpha;
    this.z = h.prevZ + (h.z - h.prevZ) * alpha;

    const t = phaseOf(h.actTick, clocks.heroRate, alpha);
    if (this.model) {
      this.armorTierBinding.update(this.model, view.stats.armorTier);
      this.weaponTierBinding.update(this.model, view.stats.weaponTier);
      this.updateModel(view, alpha, clocks, t);
      return;
    }
    const gt = clocks.heroNow;
    let swYaw = IDLE_YAW, swPitch = IDLE_PITCH, lean = 0, yOff = 0, squash = 1, spin = 0;
    let emisHex = 0xff2a3d, emisI = 0;
    let sharp = 16;
    let capeTarget = 0.12;

    switch (h.act) {
      case HeroAct.Idle:
        yOff = Math.sin(gt * 0.07) * 0.015;
        break;
      case HeroAct.Run: {
        const ph = gt * 0.5;
        lean = 0.16;
        yOff = Math.abs(Math.sin(ph)) * 0.07;
        swYaw = IDLE_YAW + Math.sin(ph) * 0.18;
        swPitch = 0.7;
        capeTarget = 0.6;
        break;
      }
      case HeroAct.Attack: {
        const s = COMBO[h.comboStep] ?? COMBO[0];
        const half = s.arc > Math.PI * 1.9 ? 1.25 : s.arc / 2;
        const a0 = h.comboStep % 2 === 0 ? half : -half;
        const a1 = -a0;
        sharp = 0; // 감쇠 없이 틱 타이밍 그대로(참격 타이밍이 판정과 맞아야 한다)
        if (t < s.windup) {
          const p = easeOut(clamp01(t / Math.max(1, s.windup)));
          swYaw = lerp(IDLE_YAW, a0, p);
          swPitch = lerp(IDLE_PITCH, 0.1, p);
          lean = -0.14 * p;
          if (s.finisher) squash = 1 - 0.1 * p;
        } else if (t < s.windup + s.active) {
          const p = clamp01((t - s.windup) / Math.max(1, s.active));
          swYaw = s.finisher ? 0 : lerp(a0, a1, easeOut(p));
          swPitch = 0.05;
          lean = 0.22;
          if (s.finisher) spin = TAU * easeOut(p);
        } else {
          const p = clamp01((t - s.windup - s.active) / Math.max(1, s.recovery));
          const e = easeInOut(p);
          swYaw = s.finisher ? lerp(0, IDLE_YAW, e) : lerp(a1, IDLE_YAW, e);
          swPitch = lerp(0.05, IDLE_PITCH, e);
          lean = 0.22 * (1 - e);
        }
        break;
      }
      case HeroAct.Dodge: {
        const p = clamp01(t / DODGE.duration);
        lean = TAU * easeOut(p); // 앞구르기
        squash = 0.72;
        swPitch = 1.0;
        swYaw = 0.1;
        yOff = 0.05;
        sharp = 0;
        break;
      }
      case HeroAct.Skill: {
        sharp = 0;
        const sk = SKILLS[h.skillId - 1];
        const p = clamp01(t / Math.max(1, sk ? sk.duration : 30));
        if (h.skillId === 1) {
          lean = 0.6; swYaw = 0; swPitch = 0.02;
        } else if (h.skillId === 2) {
          spin = p * 3 * TAU; swYaw = 0; swPitch = 0.02; lean = 0.08; squash = 0.96;
        } else if (h.skillId === 4 || h.skillId === 5) {
          // 진홍 초승달(·천공 올려베기: 같은 휘두름 자세를 쓴다): 선딜 0.15초(9틱)에 몸을 비틀어 가로로 크게 휘두른 뒤 천천히 돌아온다(도약 포즈로 떨어지지 않게)
          const w = SKILL_TUNING.crescentWindup;
          if (t < w) {
            const q = easeOut(clamp01(t / w));
            swYaw = lerp(IDLE_YAW, 1.25, q); swPitch = lerp(IDLE_PITCH, 0.1, q); lean = -0.14 * q;
          } else {
            const q = easeOut(clamp01((t - w) / 8));
            swYaw = lerp(1.25, -1.25, q); swPitch = 0.05; lean = 0.22 * (1 - clamp01((t - w) / 24));
          }
        } else if (p < 0.45) {
          const q = easeOut(p / 0.45);
          swPitch = lerp(IDLE_PITCH, -2.4, q); swYaw = 0; lean = -0.2 * q;
          if (h.y < 0.01) yOff = 1.6 * q;
        } else {
          const q = clamp01((p - 0.45) / 0.15);
          swPitch = lerp(-2.4, 0.9, easeOut(q)); swYaw = 0; lean = 0.35 * q;
          if (h.y < 0.01) yOff = 1.6 * (1 - clamp01((p - 0.45) / 0.3));
        }
        break;
      }
      case HeroAct.Hurt:
        lean = -0.32;
        swPitch = 0.9;
        emisHex = 0xffffff;
        emisI = 0.9 * (1 - clamp01(t / 10));
        sharp = 30;
        break;
      case HeroAct.Dead:
        lean = -1.45; yOff = -0.35; swPitch = 1.2; sharp = 9;
        break;
      case HeroAct.Victory:
        swPitch = -2.3; swYaw = 0; yOff = Math.abs(Math.sin(gt * 0.12)) * 0.12;
        break;
      default:
        break;
    }

    // 각성은 act와 별개의 타임라인(awaken.phase)이 자세를 덮어쓴다(각성 타임라인은 히트스톱 영향 없음).
    const aw = view.awaken;
    if (aw.phase !== AwakenPhase.None) {
      const pt = phaseOf(aw.phaseTick, 1, alpha);
      sharp = 18;
      switch (aw.phase) {
        case AwakenPhase.Gather:
          swPitch = -2.1; swYaw = 0; lean = -0.12; squash = 0.96;
          emisHex = 0xff2a3d; emisI = 0.2 + 1.1 * clamp01(pt / 36);
          break;
        case AwakenPhase.Flash:
        case AwakenPhase.Cutin:
          swPitch = -2.1; swYaw = 0; emisHex = 0xff5060; emisI = 1.4;
          break;
        case AwakenPhase.Barrage:
          sharp = 0;
          spin = pt * 0.55; swPitch = 0.02; swYaw = 0; lean = 0.15;
          emisHex = 0xff2a3d; emisI = 1.2;
          break;
        case AwakenPhase.Recover:
          emisHex = 0xff2a3d; emisI = 1.0 * (1 - clamp01(pt / 27));
          break;
        default:
          break;
      }
    }

    const k = sharp === 0 ? 1 : damp(sharp, dt);
    this.swYaw += (swYaw - this.swYaw) * k;
    this.swPitch += (swPitch - this.swPitch) * k;
    this.leanX += (lean - this.leanX) * (sharp === 0 ? 1 : damp(sharp * 0.8, dt));
    this.yOff += (yOff - this.yOff) * k;
    this.squash += (squash - this.squash) * k;
    this.capeX += (capeTarget - this.capeX) * damp(8, dt);

    this.turn(h.act, h.facing, dt, clocks.heroDelta / 60);

    this.root.position.set(this.x, this.y + this.yOff, this.z);
    this.root.rotation.y = this.yaw + spin;
    this.lean.rotation.x = this.leanX;
    const sq = 1 / Math.sqrt(this.squash);
    this.lean.scale.set(sq, this.squash, sq);
    this.swordPivot.rotation.set(this.swPitch, this.swYaw, 0);
    this.capePivot.rotation.x = this.capeX;
    setEmissive(this.mats, emisHex, emisI);
    this.rim.strength.value = RIM_BASE + this.rimBoost;
  }

  private updateModel(view: GameView, alpha: number, clocks: GameClocks, t: number): void {
    const h = view.hero;
    const m = this.model!;
    // 벽을 비스듬히 누를 때 입력 방향이 아니라 충돌 후 실제 이동 방향으로 몸을 돌린다.
    const dx = this.x - this.root.position.x, dz = this.z - this.root.position.z;
    const facing = this.yawInit && h.act === HeroAct.Run && clocks.heroDelta > 0 && Math.hypot(dx, dz) > 0.0001
      ? Math.atan2(dx, dz) : h.facing;
    this.turn(h.act, facing, clocks.heroDelta / 60, clocks.heroDelta / 60);
    this.root.position.set(this.x, this.y, this.z);
    this.root.rotation.y = this.yaw;
    m.setTurnError(h.act === HeroAct.Run ? angleDelta(this.yaw, facing) : 0);
    m.update(view, alpha, clocks);
    // 피격 흰 플래시(국소), 각성 붉은 발광(텍스처가 보이도록 캡슐보다 약하게)
    let hex = this.glowHex, i = 0;
    if (h.act === HeroAct.Hurt) { hex = 0xffffff; i = 0.85 * (1 - clamp01(t / 10)); }
    const aw = view.awaken;
    if (aw.phase === AwakenPhase.Gather) i = 0.08 + 0.3 * clamp01(phaseOf(aw.phaseTick, 1, alpha) / 36);
    else if (aw.phase === AwakenPhase.Flash || aw.phase === AwakenPhase.Cutin || aw.phase === AwakenPhase.Barrage) i = 0.38;
    else if (aw.phase === AwakenPhase.Recover) i = 0.38 * (1 - clamp01(phaseOf(aw.phaseTick, 1, alpha) / 27));
    m.setEmissive(hex, i);
    this.rim.strength.value = RIM_BASE + this.rimBoost;
  }

  /** 장면 전환: 방향 보간을 새로 시작한다. */
  snap(): void {
    this.yawInit = false;
    this.model?.reset();
  }

  dispose(): void {
    this.root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh && m.parent && !(m.parent as THREE.Mesh).isMesh) m.geometry.dispose();
    });
    for (const m of this.mats) m.dispose();
    this.hullMat.dispose();
  }
}
